// Transcript view (Unit 23, spec Step 14 + §11.2 + §12).
//
// When pageDimensions is provided (after the scan canvas pages render), each
// line is absolutely positioned at the same Y as its scan counterpart so
// transcript and scan stay in visual sync. Without dimensions, falls back to
// normal flow. The scroll container ref is forwarded to the parent for
// two-way scroll sync with the scan canvas.

import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { usePaperTranscript } from "../api/hooks";
import type { AnnotationType, TranscriptWord } from "../api/types";
import { WordDoubleClickInfo } from "./AnnotationOverlay";
import { useAnnotationStore } from "../state/annotationStore";

import "./TranscriptView.css";

// Matches .scan-canvas CSS: padding: 32px 20px; gap: 16px
const SCAN_PADDING = 32;
const PAGE_GAP = 16;
const PAGE_MARGIN_X = 20;
const LINE_PAD_LEFT = 60;   // wide left margin inside the page
const LINE_PAD_RIGHT = 64;  // right margin
const BASE_FONT_SIZE = 18;  // px
const MIN_FONT_SIZE = 9;    // px — below this, wrap lines instead of shrinking further

interface Props {
  paperId: number;
  /** Map from page_number (1-indexed) → canvas height in px. */
  pageDimensions?: Map<number, number>;
  /** Map from page_number (1-indexed) → footnote area height in px. */
  footnoteDimensions?: Map<number, number>;
  /** Ref forwarded to the scroll container for external scroll-sync. */
  scrollRef?: React.RefObject<HTMLDivElement>;
  /** Scroll handler for external scroll-sync. */
  onScroll?: () => void;
  /** Mirror of the toolbar visibility filter — hides annotation highlights for hidden types. */
  visibleTypes?: Set<AnnotationType>;
  onWordDoubleClick?: (info: WordDoubleClickInfo) => void;
}

interface DisplayWord {
  text: string;
  x_norm: number;
  y_norm: number;
  width_norm: number;
  height_norm: number;
  flagId?: number;
  flagType?: AnnotationType;
}

interface SyncLine {
  words: DisplayWord[];
  y_norm: number;
}

interface SyncPage {
  page_number: number;
  lines: SyncLine[];
}

export function TranscriptView({ paperId, pageDimensions, footnoteDimensions, scrollRef, onScroll, visibleTypes, onWordDoubleClick }: Props) {
  const { data, isLoading } = usePaperTranscript(paperId);
  const annotations = useAnnotationStore((s) => s.annotations);
  const select = useAnnotationStore((s) => s.select);
  const selectWord = useAnnotationStore((s) => s.selectWord);
  const selectedId = useAnnotationStore((s) => s.selectedId);
  const selectedWordKey = useAnnotationStore((s) => s.selectedWordKey);
  const internalRef = useRef<HTMLDivElement>(null);
  const canvasCtxRef = useRef<CanvasRenderingContext2D | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    const el = internalRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  function computeLineFontSize(text: string): number {
    if (containerWidth === 0) return BASE_FONT_SIZE;
    const available = containerWidth - LINE_PAD_LEFT - LINE_PAD_RIGHT;
    if (available <= 0) return BASE_FONT_SIZE;
    let ctx = canvasCtxRef.current;
    if (!ctx) {
      const canvas = document.createElement("canvas");
      ctx = canvas.getContext("2d");
      canvasCtxRef.current = ctx;
    }
    if (!ctx) return BASE_FONT_SIZE;
    ctx.font = `${BASE_FONT_SIZE}px Inter, system-ui, sans-serif`;
    const naturalWidth = ctx.measureText(text).width;
    if (naturalWidth <= available) return BASE_FONT_SIZE;
    return (BASE_FONT_SIZE * available) / naturalWidth;
  }

  const flagLookup = useMemo(() => {
    const map = new Map<string, { id: number; type: AnnotationType }>();
    for (const a of annotations) {
      if (a.status !== "pending") continue;
      if (visibleTypes != null && !visibleTypes.has(a.type)) continue;
      const key = `${a.page_number}|${a.x_norm.toFixed(3)}|${a.y_norm.toFixed(3)}`;
      const existing = map.get(key);
      if (!existing || a.type === "ocr_flag") map.set(key, { id: a.id, type: a.type });
    }
    return map;
  }, [annotations, visibleTypes]);

  // Word-position key for the currently-selected resolved annotation so the
  // transcript can show an outline on that word even though it has no pending flag.
  const selectedAnnotPos = useMemo(() => {
    if (selectedId == null) return null;
    const a = annotations.find((ann) => ann.id === selectedId && ann.status !== "pending");
    if (!a) return null;
    return `${a.page_number}|${a.x_norm.toFixed(3)}|${a.y_norm.toFixed(3)}`;
  }, [selectedId, annotations]);

  const pages: SyncPage[] = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.map((p) => ({
      page_number: p.page_number,
      lines: buildLines(
        p.words.map((w) => {
          const key = `${p.page_number}|${w.x_norm.toFixed(3)}|${w.y_norm.toFixed(3)}`;
          const flag = flagLookup.get(key);
          return { text: w.text, x_norm: w.x_norm, y_norm: w.y_norm, width_norm: w.width_norm, height_norm: w.height_norm, flagId: flag?.id, flagType: flag?.type };
        }),
        p.words,
      ),
    }));
  }, [data, flagLookup]);

  // Whether all page heights are known.
  const hasDimensions = pageDimensions != null && pageDimensions.size > 0 &&
    pages.every((p) => pageDimensions.has(p.page_number));

  // Single font size for the whole transcript — the smallest needed across all
  // lines so that no line overflows, and all lines share the same size.
  // When the raw minimum falls below MIN_FONT_SIZE, shouldWrap flips to true
  // and lines are allowed to wrap rather than shrinking the font further.
  const [unifiedFontSize, shouldWrap] = useMemo(() => {
    if (containerWidth === 0) return [BASE_FONT_SIZE, false] as const;
    let min = BASE_FONT_SIZE;
    for (const p of pages) {
      for (const line of p.lines) {
        const size = computeLineFontSize(line.words.map((w) => w.text).join(" "));
        if (size < min) min = size;
      }
    }
    const wrap = min < MIN_FONT_SIZE;
    return [wrap ? MIN_FONT_SIZE : min, wrap] as const;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerWidth, pages]);

  // Total scrollable height — must match scan canvas scroll height exactly.
  const totalHeight = useMemo(() => {
    if (!hasDimensions || !pageDimensions) return undefined;
    let h = SCAN_PADDING;
    for (let i = 0; i < pages.length; i++) {
      h += (pageDimensions.get(pages[i].page_number) ?? 0)
         + (footnoteDimensions?.get(pages[i].page_number) ?? 0);
      if (i < pages.length - 1) h += PAGE_GAP;
    }
    h += SCAN_PADDING;
    return h;
  }, [hasDimensions, pageDimensions, footnoteDimensions, pages]);

  // Top offset of the page's top edge within the container.
  // Each preceding page contributes its canvas height + footnote height + gap.
  function pageTopOffset(pageNumber: number): number {
    if (!pageDimensions) return 0;
    let top = SCAN_PADDING;
    for (const p of pages) {
      if (p.page_number === pageNumber) break;
      top += (pageDimensions.get(p.page_number) ?? 0)
           + (footnoteDimensions?.get(p.page_number) ?? 0)
           + PAGE_GAP;
    }
    return top;
  }

  // Top offset for a line within the container.
  function lineTop(pageNumber: number, y_norm: number): number {
    return pageTopOffset(pageNumber) + y_norm * (pageDimensions?.get(pageNumber) ?? 0);
  }

  // Merge external ref + internal ref.
  function attachRef(el: HTMLDivElement | null) {
    (internalRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    if (scrollRef) (scrollRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
  }

  if (isLoading) {
    return <div className="transcript transcript--empty" ref={attachRef} onScroll={onScroll}><p>Loading…</p></div>;
  }
  if (!data || pages.length === 0) {
    return <div className="transcript transcript--empty" ref={attachRef} onScroll={onScroll}><p>No transcript available yet.</p></div>;
  }

  if (hasDimensions && totalHeight != null) {
    // Synced layout: page backgrounds + absolutely-positioned lines.
    // The outer div is the scroll container (height from flex); the inner div
    // carries totalHeight so it overflows and makes scrollTop meaningful.
    return (
      <div
        className="transcript transcript--synced"
        ref={attachRef}
        onScroll={onScroll}
      >
        <div style={{ position: "relative", height: totalHeight, fontSize: unifiedFontSize }}>
          {/* Page paper backgrounds — must come first in DOM so lines render on top */}
          {pages.map((p) => (
            <div
              key={`bg-${p.page_number}`}
              className="transcript__page-bg"
              style={{
                position: "absolute",
                top: pageTopOffset(p.page_number),
                left: PAGE_MARGIN_X,
                right: PAGE_MARGIN_X,
                height: pageDimensions!.get(p.page_number) ?? 0,
              }}
            />
          ))}
          {/* Text lines */}
          {pages.flatMap((p) =>
            p.lines.map((line, li) => (
              <div
                key={`${p.page_number}-${li}`}
                className="transcript__line"
                style={{ position: "absolute", top: lineTop(p.page_number, line.y_norm), left: LINE_PAD_LEFT, right: LINE_PAD_RIGHT, whiteSpace: shouldWrap ? "normal" : undefined, transform: shouldWrap ? "none" : undefined }}
              >
                {renderWords(line.words, p.page_number, selectedId, selectedWordKey, selectedAnnotPos, select, selectWord, onWordDoubleClick)}
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // Fallback: normal flow layout before page heights are known.
  return (
    <div className="transcript" ref={attachRef} onScroll={onScroll}>
      {pages.map((p, pi) => (
        <div key={pi} className="transcript__page">
          {p.lines.map((line, li) => (
            <div key={li} className="transcript__line">
              {renderWords(line.words, p.page_number, selectedId, selectedWordKey, selectedAnnotPos, select, selectWord)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function renderWords(
  words: DisplayWord[],
  pageNumber: number,
  selectedId: number | null,
  selectedWordKey: string | null,
  selectedAnnotPos: string | null,
  select: (id: number | null) => void,
  selectWord: (key: string | null) => void,
  onWordDoubleClick?: (info: WordDoubleClickInfo) => void,
) {
  return words.map((w, wi) => {
    const wordKey = `${pageNumber}|${w.x_norm.toFixed(3)}|${w.y_norm.toFixed(3)}`;
    const isAnnotSelected =
      (w.flagId != null && w.flagId === selectedId) ||
      (w.flagId == null && wordKey === selectedAnnotPos);
    const isWordSelected = !isAnnotSelected && selectedWordKey === wordKey;

    const flagClass =
      w.flagType === "ocr_flag" ? " transcript__tok--ocr" :
      w.flagType != null ? " transcript__tok--lang-flag" : "";

    const selClass =
      isAnnotSelected
        ? (w.flagType === "ocr_flag" ? " transcript__tok--ocr-selected" : " transcript__tok--annot-selected")
        : isWordSelected ? " transcript__tok--word-selected" : "";

    return (
      <span
        key={wi}
        className={"transcript__tok" + flagClass + selClass}
        role="button"
        onClick={(e) => {
          if (w.flagId != null) {
            select(w.flagId);
          } else if (isWordSelected) {
            selectWord(null);
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            onWordDoubleClick?.({
              pageNumber,
              xNorm: w.x_norm,
              yNorm: w.y_norm,
              widthNorm: w.width_norm,
              heightNorm: w.height_norm,
              screenX: rect.left,
              screenY: rect.bottom + 8,
            });
          } else {
            selectWord(wordKey);
          }
        }}
      >
        {w.text}{" "}
      </span>
    );
  });
}

function buildLines(words: DisplayWord[], positions: TranscriptWord[]): SyncLine[] {
  if (words.length === 0) return [];
  const lines: SyncLine[] = [];
  let currentLine: DisplayWord[] = [];
  let bandY = -1;
  let lineYNorm = 0;

  for (let i = 0; i < words.length; i++) {
    const y = positions[i].y_norm;
    if (bandY < 0 || Math.abs(y - bandY) <= 0.02) {
      currentLine.push(words[i]);
      if (bandY < 0) { bandY = y; lineYNorm = y; }
    } else {
      if (currentLine.length > 0) lines.push({ words: currentLine, y_norm: lineYNorm });
      currentLine = [words[i]];
      bandY = y;
      lineYNorm = y;
    }
  }
  if (currentLine.length > 0) lines.push({ words: currentLine, y_norm: lineYNorm });
  return lines;
}
